import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React from 'react';
import { ButtonMinusIcon, ButtonPlusIcon } from '@manulife/mux-cds-icons';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Errors from '../Errors/Errors.js';
import { SVG_ICON_SIZE_DEFAULT } from '../Icon/constants.js';
import i18n from '../../lang/i18n/index.js';
import { hasErrors, noop, rem } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import { Root, Wrapper, PlusMinusButton, ValueIndicator } from './styledComponents.js';
import { formatNumber, decreaseValue, increaseValue } from './utils.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';

var PlusMinus = function PlusMinus(_ref) {
  var controlledId = _ref.id,
      value = _ref.value,
      increment = _ref.increment,
      minimum = _ref.minimum,
      maximum = _ref.maximum,
      minusIcon = _ref.minusIcon,
      plusIcon = _ref.plusIcon,
      onChange = _ref.onChange,
      disabled = _ref.disabled,
      errors = _ref.errors,
      errorCallout = _ref.errorCallout,
      ariaLabelMinus = _ref.ariaLabelMinus,
      ariaLabelPlus = _ref.ariaLabelPlus;

  var _useDerivedState = useDerivedState(useId('plusminus-id-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var lang = useLang();
  var theme = useTheme();
  var hasError = hasErrors(errors);
  var wrappedOnChange = disabled ? noop : handle(onChange);

  var handleDecrease = function handleDecrease(event) {
    return wrappedOnChange(event, decreaseValue(value, increment, minimum));
  };

  var handleIncrease = function handleIncrease(event) {
    return wrappedOnChange(event, increaseValue(value, increment, maximum));
  };

  return /*#__PURE__*/React.createElement(Root, {
    id: id
  }, /*#__PURE__*/React.createElement(Wrapper, {
    "aria-live": "polite",
    "aria-valuemax": maximum,
    "aria-valuemin": minimum,
    "aria-valuenow": value
  }, /*#__PURE__*/React.createElement(PlusMinusButton, {
    id: "".concat(id, "-minus"),
    type: "button",
    onClick: handleDecrease,
    disabled: disabled,
    "aria-label": ariaLabelMinus || i18n[lang].plusMinus.ariaLabelMinusDefault,
    theme: theme,
    error: hasError
  }, minusIcon || /*#__PURE__*/React.createElement(ButtonMinusIcon, {
    fontSize: rem(SVG_ICON_SIZE_DEFAULT)
  })), /*#__PURE__*/React.createElement(ValueIndicator, {
    id: "".concat(id, "-amount"),
    error: hasError,
    theme: theme
  }, formatNumber(value, lang)), /*#__PURE__*/React.createElement(PlusMinusButton, {
    id: "".concat(id, "-plus"),
    onClick: handleIncrease,
    disabled: disabled,
    "aria-label": ariaLabelPlus || i18n[lang].plusMinus.ariaLabelPlusDefault,
    type: "button",
    theme: theme,
    error: hasError
  }, plusIcon || /*#__PURE__*/React.createElement(ButtonPlusIcon, {
    fontSize: rem(SVG_ICON_SIZE_DEFAULT)
  }))), /*#__PURE__*/React.createElement(Errors, {
    id: id,
    errors: errors,
    callouts: errorCallout
  }));
};

process.env.NODE_ENV !== "production" ? PlusMinus.reduxFormProps = {
  inputProp: 'value',
  inputType: 'number'
} : void 0;
process.env.NODE_ENV !== "production" ? PlusMinus.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,

  /** Number value to display in the box... should be updated using onChange */
  value: PropTypes.number,

  /** amount to increment by, new value is passed to onchange handler */
  increment: PropTypes.number,

  /** Minimum amount to increment to */
  minimum: PropTypes.number,

  /** Maximum amount to increment to */
  maximum: PropTypes.number,

  /**
   * Icon displayed on the minus button.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/?path=/docs/foundations-icon-cds-icons) /
   * [Manulife icons](/?path=/docs/foundations-icon-manulife-icons) /
   * [Custom icons](/?path=/docs/foundations-icon-custom-icons).
   * */
  minusIcon: PropTypes.node,

  /**
   * Icon displayed on the plus button.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/?path=/docs/foundations-icon-cds-icons) /
   * [Manulife icons](/?path=/docs/foundations-icon-manulife-icons) /
   * [Custom icons](/?path=/docs/foundations-icon-custom-icons).
   * */
  plusIcon: PropTypes.node,

  /** Aria Label for the minus button */
  ariaLabelMinus: PropTypes.string,

  /** Aria Label for the plus button */
  ariaLabelPlus: PropTypes.string,

  /** User will not be able to increase/decrease number if control is disabled */
  disabled: PropTypes.bool,

  /** Errors to display */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    // error id
    msg: PropTypes.any,
    // localized error text
    isError: PropTypes.bool // error condition

  })),

  /** Should errors be read out as alerts when using a screen reader? */
  errorCallout: PropTypes.bool,

  /** onChange handler... only parameter passed will be the new value */
  onChange: PropTypes.func
} : void 0;
PlusMinus.defaultProps = {
  id: undefined,
  value: 0,
  increment: 1,
  minimum: 0,
  maximum: 10,
  minusIcon: undefined,
  plusIcon: undefined,
  ariaLabelMinus: undefined,
  ariaLabelPlus: undefined,
  disabled: false,
  errors: [],
  errorCallout: false,
  onChange: noop
};
process.env.NODE_ENV !== "production" ? PlusMinus.displayName = 'PlusMinus' : void 0;
var PlusMinus$1 = PlusMinus;

export { PlusMinus$1 as default };
